import { Flex, SidePanel, Tag, Text } from "@wayflyer/flyui";
import { IconPage24Line } from "@wayflyer/flyui-icons/24/line";
import type { BankAccountGroupSchema, UngroupedStatementSchema } from "bankconnect";
import { useState } from "react";
import { useIntl } from "react-intl";

import { useRemoveStatementAction } from "../../hooks/use-remove-statement-action";
import { AssignStatementModal } from "../assign-statement-modal/assign-statement-modal";
import { StatementRow } from "../components/statement-row";
import { MESSAGES } from "../message-definitions";

type UnmatchedStatementsPanelProps = {
  isOpen: boolean;
  statements: UngroupedStatementSchema[];
  accountGroups: BankAccountGroupSchema[];
  companyDomain: string;
  hasBankAccounts: boolean;
  onClose: () => void;
  onStatementsChanged: () => void;
};

export const UnmatchedStatementsPanel = ({
  isOpen,
  statements,
  accountGroups,
  companyDomain,
  hasBankAccounts,
  onClose,
  onStatementsChanged,
}: UnmatchedStatementsPanelProps) => {
  const intl = useIntl();
  const { removeStatement, isPending: isRemovePending } = useRemoveStatementAction(companyDomain);
  const [statementToReassign, setStatementToReassign] = useState<UngroupedStatementSchema | null>(null);

  if (statements.length === 0) {
    return null;
  }

  // Moving or removing the last unmatched statement empties this list, so close the panel instead
  // of leaving it open on an empty state - the parent's isOpen flag otherwise survives the list
  // emptying out, and a later unmatched upload would reopen it with no click.
  const handleStatementResolved = () => {
    onStatementsChanged();
    if (statements.length <= 1) {
      onClose();
    }
  };

  return (
    <>
      <SidePanel
        open={isOpen}
        onClose={onClose}
        title={
          hasBankAccounts
            ? intl.formatMessage(MESSAGES.otherStatementsUploadedLabel)
            : intl.formatMessage(MESSAGES.statementsUploadedCountLabel, { count: statements.length })
        }
        variant="md"
      >
        <Flex direction="column" gap="4">
          <Flex justify="space-between" align="flex-start">
            <Flex gap="2">
              <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.sourcesSectionTitle)}</Text>
              <Text color="subtle">{statements.length}</Text>
            </Flex>
            <Tag content={intl.formatMessage(MESSAGES.readyBadge)} color="positive" />
          </Flex>

          <Text size="sm">{intl.formatMessage(MESSAGES.unmatchedStatementsPanelBodyText)}</Text>

          <Flex direction="column" gap="2">
            {statements.map((statement) => (
              <StatementRow
                key={statement.id}
                label={statement.fileName}
                logo={<IconPage24Line />}
                onReassign={() => setStatementToReassign(statement)}
                onRemove={() =>
                  removeStatement(statement.id, { wasUnassigned: true, onSuccess: handleStatementResolved })
                }
                isRemoveDisabled={isRemovePending}
              />
            ))}
          </Flex>
        </Flex>
      </SidePanel>

      <AssignStatementModal
        key={`assign-${statementToReassign?.id ?? "none"}`}
        isOpen={statementToReassign !== null}
        onClose={() => setStatementToReassign(null)}
        fileName={statementToReassign?.fileName ?? ""}
        uploadUuid={statementToReassign?.id ?? ""}
        currentGroupUuid={null}
        accountGroups={accountGroups}
        companyDomain={companyDomain}
        onMoved={handleStatementResolved}
      />
    </>
  );
};
